import { cn } from "utils/ui";

/** Default / minimum SideBar open width on lg+ (rem). Never animate Framer to content `"auto"`. */
export const SIDEBAR_LG_WIDTH_MIN = "36rem";
export const SIDEBAR_LG_WIDTH_MIN_PX = 36 * 16;

/** Hard cap before viewport clamp (rem). */
export const SIDEBAR_LG_WIDTH_MAX = "48rem";
export const SIDEBAR_LG_WIDTH_MAX_PX = 48 * 16;

/** Default open width alias (same as min) for Framer before the tab strip reports. */
export const SIDEBAR_LG_WIDTH = SIDEBAR_LG_WIDTH_MIN;

/**
 * Steady-state Tailwind width on lg+ — CSS var set by SideBar from the tab strip measure.
 * Literal class so Tailwind JIT keeps it; fallback matches SIDEBAR_LG_WIDTH_MIN.
 */
export const SIDEBAR_LG_WIDTH_CLASS = "lg:w-[var(--sidebar-lg-width,36rem)]";

/** Tailwind `lg`: from here the list and the SideBar sit side by side; below it the SideBar replaces the list. */
export const MASTER_DETAIL_SIDE_BY_SIDE_MEDIA_QUERY = "(min-width: 1024px)";

/** Shared Offers / Tasks / Requests master-detail row (list + SideBar). */
export const masterDetailRowClassName = "flex flex-row gap-4 px-4";

/** `column` stacks the filters above an inner scroller that takes the remaining height (task list). */
export function masterDetailMasterClassName(isDetailOpen: boolean, layout: "block" | "column" = "block") {
  const isColumn = layout === "column";
  return cn("min-w-0 flex-1 py-4", {
    "flex min-h-0 flex-col": isColumn,
    "hidden lg:block": isDetailOpen && !isColumn,
    "hidden lg:flex": isDetailOpen && isColumn,
  });
}

/**
 * SideBar tab strip: stay one row on sm+, never wrap (height stays stable).
 * When labels still overflow the clamped shell (e.g. long FR + max width), scroll horizontally.
 */
export const sideBarTabsListClassName =
  "mx-2 flex w-full min-w-0 flex-col items-stretch gap-2 overflow-x-auto border-y border-gray-300 py-4 dark:border-gray-600 sm:mx-0 sm:flex-row sm:flex-nowrap sm:items-center sm:justify-start sm:gap-0 sm:overflow-x-auto sm:border-y-0 sm:pb-0 [scrollbar-width:thin]";

/** Overrides TabsTrigger `mx-4` so several long labels fit / scroll inside the panel. */
export const sideBarTabsTriggerClassName = "mx-0 shrink-0 px-2 sm:mx-1";

/**
 * Comfort slack on top of the intrinsic tab strip so `overflow-x-auto` does not show a thin
 * scrollbar from subpixel / border chrome. Tabs row uses `-mx-6` (full-bleed of the padded
 * panel), so strip ≈ inner shell; shell still needs a little margin beyond scrollWidth.
 */
export const SIDEBAR_TABS_WIDTH_SLACK_PX = 32; // 2rem

/** Clamp measured tab-strip need into [min, max] rem (px). Parent `lg:max-w-full` still caps by layout. */
export function clampSidebarLgWidthPx(neededPx: number): number {
  return Math.min(Math.max(neededPx, SIDEBAR_LG_WIDTH_MIN_PX), SIDEBAR_LG_WIDTH_MAX_PX);
}

/** Convert measured TabsList width → shell width (strip + slack, then clamp). */
export function shellWidthFromTabsStripPx(stripWidthPx: number): number {
  return clampSidebarLgWidthPx(stripWidthPx + SIDEBAR_TABS_WIDTH_SLACK_PX);
}

export function formatSidebarLgWidthRem(widthPx: number): string {
  return `${widthPx / 16}rem`;
}

/**
 * Intrinsic tab-strip width: force row + max-content so flex-col / w-full do not skew the measure.
 * Call from useLayoutEffect (sync, before paint). Does not include shell slack — use
 * `shellWidthFromTabsStripPx` when applying to SideBar width.
 */
export function measureTabsStripWidthPx(listEl: HTMLElement): number {
  const prevWidth = listEl.style.width;
  const prevFlexDirection = listEl.style.flexDirection;
  const prevFlexWrap = listEl.style.flexWrap;
  listEl.style.width = "max-content";
  listEl.style.flexDirection = "row";
  listEl.style.flexWrap = "nowrap";
  const width = listEl.scrollWidth;
  listEl.style.width = prevWidth;
  listEl.style.flexDirection = prevFlexDirection;
  listEl.style.flexWrap = prevFlexWrap;
  return width;
}
